import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Department scope for a report.
 *
 * Behaviour carried over verbatim from the payroll reports screen, because it is the behaviour HR
 * already relies on: no selection means ALL departments (the request omits the filter entirely),
 * "Unassigned" is a real option carrying id 0, and a clear link resets to all. The markup is a
 * toggle-chip list rather than the old checkbox-in-a-label, so selection is visible from the chip
 * itself and no nested-label click handling is involved.
 */

export interface DepartmentOption {
  id: number;
  name: string;
}

/** Employees with no department. The server treats department id 0 as "unassigned". */
export const UNASSIGNED_DEPARTMENT_ID = 0;

export interface DepartmentFilterProps {
  departments: DepartmentOption[];
  /** Selected ids. Empty means every department, which is what the report defaults to. */
  value: number[];
  onChange: (ids: number[]) => void;
  /** Localized name for the id-0 bucket. */
  unassignedLabel: string;
  /** Show a search box once the list is longer than this. */
  searchThreshold?: number;
  disabled?: boolean;
  className?: string;
  testId?: string;
}

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50';
const CHIP_ACTIVE = 'border-primary bg-primary/10 text-primary';
const CHIP_IDLE =
  'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground';

export function DepartmentFilter({
  departments,
  value,
  onChange,
  unassignedLabel,
  searchThreshold = 12,
  disabled = false,
  className,
  testId,
}: DepartmentFilterProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const options = useMemo(
    () => [...departments, { id: UNASSIGNED_DEPARTMENT_ID, name: unassignedLabel }],
    [departments, unassignedLabel],
  );

  const searchable = options.length > searchThreshold;

  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!searchable || !needle) return options;
    return options.filter((o) => o.name.toLocaleLowerCase().includes(needle));
  }, [options, query, searchable]);

  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  if (departments.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)} data-testid={testId}>
        {t('reports.noDepartments')}
      </p>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid={testId}>
      {searchable && (
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            disabled={disabled}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('reports.searchDepartments')}
            placeholder={t('reports.searchDepartments')}
            data-testid="department-search"
            className="w-full rounded-md border border-input bg-background py-1.5 pe-3 ps-9 text-sm transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('reports.departments')}>
        <button
          type="button"
          disabled={disabled}
          aria-pressed={value.length === 0}
          onClick={() => onChange([])}
          data-testid="department-all"
          className={cn(CHIP_BASE, value.length === 0 ? CHIP_ACTIVE : CHIP_IDLE)}
        >
          {value.length === 0 && <Check className="h-3 w-3 shrink-0" aria-hidden="true" />}
          {t('reports.allDepartments')}
        </button>

        {visible.map((option) => {
          const checked = value.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              disabled={disabled}
              aria-pressed={checked}
              onClick={() => toggle(option.id)}
              data-testid={`department-option-${option.id}`}
              className={cn(CHIP_BASE, checked ? CHIP_ACTIVE : CHIP_IDLE)}
            >
              {checked && <Check className="h-3 w-3 shrink-0" aria-hidden="true" />}
              {option.name}
            </button>
          );
        })}

        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('reports.noMatchingDepartments')}</p>
        )}
      </div>

      {value.length > 0 && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange([])}
          data-testid="department-clear"
          className="self-start text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          {t('reports.clearDepartments')}
        </button>
      )}
    </div>
  );
}

export default DepartmentFilter;
